import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Target, Building, DollarSign, Calendar, TrendingUp, User, Save, Loader2, Package, Plus } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { Opportunity, Company, Product } from '../../types';
// mockCompanies removed for production-only data
import { companiesService } from '../../services/companiesService';
import { productsService } from '../../services/productsService';
import { usersService } from '../../services/usersService';
import { pricingComponentsFor, serviceCategoryOf } from '../../utils/productPricing';

interface CreateOpportunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateOpportunity: (opportunityData: Omit<Opportunity, 'id' | 'createdAt' | 'updatedAt'>) => void;
}

const CreateOpportunityModal: React.FC<CreateOpportunityModalProps> = ({ isOpen, onClose, onCreateOpportunity }) => {
  useEscapeKey(onClose, isOpen);

  const [formData, setFormData] = useState({
    title: '',
    companyId: '',
    value: '',
    probability: '50',
    expectedCloseDate: '',
    priority: 'medium' as 'low' | 'medium' | 'high',
    source: 'inbound' as 'inbound' | 'outbound' | 'referral',
    description: '',
    productId: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [useProductValue, setUseProductValue] = useState<boolean>(true);
  const [billingMonths, setBillingMonths] = useState<number>(12);
  // For consulting/development: which pricing component (rate-card level / package) drives the value, and day count.
  const [pricingBasisIdx, setPricingBasisIdx] = useState<number>(0);
  const [units, setUnits] = useState<number>(1);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [newCompany, setNewCompany] = useState<Partial<Company>>({ name: '', industry: '', website: '', domain: '', employeeCount: '', revenue: '' });

  // Format currency helper function
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  React.useEffect(() => {
    const load = async () => {
      try {
        const orgId = await usersService.getCurrentUserOrgId();
        const list = await companiesService.getCompanies(orgId || undefined);
        setCompanies(list);

        // One-time cleanup: remove all "Default Service" entries
        await productsService.removeDefaultServices(orgId || undefined);

        // Ensure "Nach Kundenwunsch" product exists
        let productsList = await productsService.list();
        const hasNachKundenwunsch = productsList.some((p: Product) => p.name === 'Nach Kundenwunsch');
        if (!hasNachKundenwunsch) {
          await productsService.create({
            key: 'nach-kundenwunsch',
            name: 'Nach Kundenwunsch',
            type: 'service',
            leadStages: ['untouched', 'interested', 'demo', 'offer', 'closing', 'won', 'lost'],
            opportunityStages: ['prospecting', 'qualification', 'proposal', 'negotiation', 'closing', 'won', 'lost'],
            isDefault: true,
          } as any, orgId || undefined);
          productsList = await productsService.list();
        }

        setProducts(productsList || []);
        
        // Set default product if available
        const defaultProduct = productsList.find((p: Product) => p.isDefault);
        if (defaultProduct) {
          setFormData(prev => ({ ...prev, productId: defaultProduct.id }));
        }
      } catch (e) {
        console.error('Error loading data:', e);
      }
    };
    if (isOpen) load();
  }, [isOpen]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const computeProductBasedValue = (product?: Product, months?: number) => {
    if (!product) return undefined;
    const p = product.pricing;
    const cat = serviceCategoryOf(product);

    // Managed service / recurring: fee × term + one-time setup fee.
    if (cat === 'managed_service' || p?.model === 'recurring') {
      const base = p?.amount ?? p?.range?.max;
      if (base == null) return (p?.setupFee || 0) || undefined;
      const m = Math.max(1, Number(months) || 1);
      const period = p?.billingPeriod || 'month';
      let recurring = base * m;
      if (period === 'year') recurring = base * (m / 12);
      else if (period === 'quarter') recurring = base * (m / 3);
      else if (period === 'week') recurring = base * (m * 4);
      return recurring + (p?.setupFee || 0);
    }

    // Consulting / development: derive from the selected pricing component (rate card level × days, or fixed package).
    const comps = pricingComponentsFor(product);
    if (comps.length > 0) {
      const comp = comps[Math.min(pricingBasisIdx, comps.length - 1)];
      const qty = comp.component === 'day_rate' ? Math.max(1, Number(units) || 1) : 1;
      return comp.unitPrice * qty;
    }

    const base = p?.amount ?? p?.range?.max;
    return base ?? undefined;
  };

  // Keep the value field in sync for preview when using product pricing
  React.useEffect(() => {
    const selectedProduct = products.find(p => p.id === formData.productId);
    if (useProductValue && selectedProduct) {
      const v = computeProductBasedValue(selectedProduct, billingMonths);
      if (v != null) setFormData(prev => ({ ...prev, value: String(Math.round(v)) }));
    }
  }, [useProductValue, formData.productId, billingMonths, pricingBasisIdx, units, products.length]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Opportunity title is required';
    }
    if (!formData.companyId) {
      newErrors.companyId = 'Please select a company';
    }
    if (!formData.productId) {
      newErrors.productId = 'Please select a product';
    }
    const selectedProduct = products.find(p => p.id === formData.productId);
    if (!(useProductValue && selectedProduct)) {
      // Value is now optional - only validate if provided
      if (formData.value.trim() && (isNaN(Number(formData.value)) || Number(formData.value) < 0)) {
        newErrors.value = 'Please enter a valid positive number';
      }
    }
    if (!formData.expectedCloseDate) {
      newErrors.expectedCloseDate = 'Expected close date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Get selected company
      const selectedCompany = companies.find(c => c.id === formData.companyId);
      if (!selectedCompany) {
        throw new Error('Selected company not found');
      }
      const selectedProduct = products.find(p => p.id === formData.productId);

      let assignedUserId = '';
      let assignedUserName = '';
      try {
        const currentProfile = await usersService.getCurrentUserProfile();
        if (currentProfile?.id) {
          assignedUserId = currentProfile.id;
          assignedUserName = currentProfile.name || currentProfile.email || '';
        }
      } catch (error) {
        console.warn('Could not get current user profile:', error);
      }

      // Create new opportunity data
      const newOpportunity: Omit<Opportunity, 'id' | 'createdAt' | 'updatedAt'> = {
        title: formData.title.trim(),
        companyId: formData.companyId,
        company: selectedCompany,
        productId: selectedProduct?.id,
        product: selectedProduct ? { id: selectedProduct.id, key: selectedProduct.key, name: selectedProduct.name, type: selectedProduct.type, serviceCategory: serviceCategoryOf(selectedProduct), leadStages: selectedProduct.leadStages, opportunityStages: selectedProduct.opportunityStages, pricing: selectedProduct.pricing } : undefined,
        leadIds: [],
        leads: [],
        stage: 'prospecting',
        value: useProductValue && selectedProduct 
          ? (computeProductBasedValue(selectedProduct, billingMonths) ?? (formData.value.trim() ? Number(formData.value) : 0))
          : (formData.value.trim() ? Number(formData.value) : 0),
        valueSource: useProductValue && selectedProduct ? 'product' : 'custom',
        billingTermMonths: useProductValue && selectedProduct?.pricing?.model === 'recurring' ? Math.max(1, Number(billingMonths) || 1) : undefined,
        probability: Number(formData.probability),
        expectedCloseDate: new Date(formData.expectedCloseDate),
        assignedTo: assignedUserId,
        assignedToName: assignedUserName,
        source: formData.source,
        priority: formData.priority,
        nextFollowUp: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        summary: {
          totalLeads: 0,
          averageScore: 0,
          highestScore: 0,
          contactPoints: 0,
          lastActivity: new Date(),
          keyDecisionMakers: []
        },
        activities: [
          {
            id: Date.now().toString(),
            type: 'stage_changed',
            description: 'Opportunity created',
            createdAt: new Date(),
            createdBy: 'System'
          }
        ],
        notes: []
      };

      await onCreateOpportunity(newOpportunity);
      handleClose();
    } catch (error) {
      console.error('Error creating opportunity:', error);
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to create opportunity. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      companyId: '',
      value: '',
      probability: '50',
      expectedCloseDate: '',
      priority: 'medium',
      source: 'inbound',
      description: '',
      productId: products.find(p => p.isDefault)?.id || ''
    });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={handleClose} />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div role="dialog" aria-modal="true" aria-label="Create New Opportunity" className="relative bg-bg-card backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-heavy w-full max-w-2xl max-h-[95vh] overflow-y-auto transform transition-all">
          {/* Header */}
          <div className="sticky top-0 bg-bg-card backdrop-blur-sm z-10 flex items-center justify-between p-4 sm:p-6 border-b border-subtle">
            <div className="flex items-center space-x-3">
              <button onClick={handleClose} className="p-2 bg-bg-surface rounded-lg sm:hidden" aria-label="Back">
                <X className="w-5 h-5 text-txt-secondary" />
              </button>
              <div className="p-2 bg-success-soft rounded-lg hidden sm:block"><Target className="w-5 h-5 text-success" /></div>
              <div>
                <h3 className="text-lg font-bold text-txt">Create New Opportunity</h3>
                <p className="text-sm text-txt-secondary">Add a new sales opportunity to your pipeline</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-bg-surface rounded-lg transition-colors hidden sm:block" disabled={isSubmitting}><X className="w-5 h-5 text-txt-muted" /></button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
            {errors.submit && (
              <div className="p-3 bg-danger-soft border border-danger rounded-lg">
                <p className="text-danger text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Product */}
            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">
                Product / Service *
              </label>
              <div className="relative">
                <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                <select
                  value={formData.productId}
                  onChange={(e)=>{ handleInputChange('productId', e.target.value); setPricingBasisIdx(0); }}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-accent/30 transition-all appearance-none bg-bg-card ${
                    errors.productId ? 'border-danger bg-danger-soft' : 'border-strong'
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">Select a product...</option>
                  {products.length > 0 ? (
                    products.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({serviceCategoryOf(p) === 'managed_service' ? 'Managed Service' : serviceCategoryOf(p) === 'consulting' ? 'Consulting' : 'Development'})
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading products...</option>
                  )}
                </select>
              </div>
              {errors.productId && (
                <p className="mt-1 text-sm text-danger">{errors.productId}</p>
              )}
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-txt-tertiary">Stages will follow the selected product's stage pattern.</p>
                <label className="text-xs text-txt-secondary flex items-center space-x-2">
                  <input type="checkbox" checked={useProductValue} onChange={(e)=>setUseProductValue(e.target.checked)} />
                  <span>Use product pricing</span>
                </label>
              </div>
              {(() => {
                const sp = products.find(p => p.id === formData.productId);
                if (!useProductValue || !sp) return null;
                const cat = serviceCategoryOf(sp);
                const isRecurring = cat === 'managed_service' || sp.pricing?.model === 'recurring';
                if (isRecurring) {
                  return (
                    <div className="mt-3 flex items-center flex-wrap gap-3">
                      <label className="text-xs text-txt-secondary">Laufzeit (Monate)</label>
                      <input
                        type="number"
                        min={1}
                        value={billingMonths}
                        onChange={(e)=>setBillingMonths(Math.max(1, Number(e.target.value) || 1))}
                        className="w-28 px-3 py-2 border border-strong rounded-lg bg-bg-card"
                      />
                      {!!sp.pricing?.setupFee && (
                        <span className="text-xs text-txt-tertiary">inkl. einmaliger Setup-Gebühr</span>
                      )}
                    </div>
                  );
                }
                // consulting / development → choose pricing basis
                const comps = pricingComponentsFor(sp);
                if (comps.length === 0) return null;
                const active = comps[Math.min(pricingBasisIdx, comps.length - 1)];
                return (
                  <div className="mt-3 flex items-center flex-wrap gap-3">
                    <label className="text-xs text-txt-secondary">Preisbasis</label>
                    <select
                      value={Math.min(pricingBasisIdx, comps.length - 1)}
                      onChange={(e)=>setPricingBasisIdx(Number(e.target.value))}
                      className="px-3 py-2 border border-strong rounded-lg bg-bg-card text-sm"
                    >
                      {comps.map((c, i) => (
                        <option key={i} value={i}>{c.label} — {formatCurrency(c.unitPrice)}</option>
                      ))}
                    </select>
                    {active?.component === 'day_rate' && (
                      <>
                        <label className="text-xs text-txt-secondary">Tage</label>
                        <input
                          type="number"
                          min={1}
                          value={units}
                          onChange={(e)=>setUnits(Math.max(1, Number(e.target.value) || 1))}
                          className="w-24 px-3 py-2 border border-strong rounded-lg bg-bg-card"
                        />
                      </>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">
                Opportunity Title *
              </label>
              <div className="relative">
                <Target className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all ${
                    errors.title ? 'border-danger bg-danger-soft' : 'border-strong'
                  }`}
                  placeholder="e.g., TechCorp CRM Implementation"
                  disabled={isSubmitting}
                />
              </div>
              {errors.title && (
                <p className="mt-1 text-sm text-danger">{errors.title}</p>
              )}
            </div>

            {/* Company Selection */}
            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">
                Company *
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                  <select
                    value={formData.companyId}
                    onChange={(e) => handleInputChange('companyId', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all appearance-none bg-bg-card ${
                      errors.companyId ? 'border-danger bg-danger-soft' : 'border-strong'
                    }`}
                    disabled={isSubmitting}
                  >
                    <option value="">Select a company...</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name} ({company.industry})
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => setIsCompanyModalOpen(true)}
                  className="px-3 py-3 bg-accent-soft text-accent border border-accent/30 rounded-xl hover:bg-accent-medium transition-colors shrink-0"
                  title="Create new company"
                >
                  <Plus className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
              {errors.companyId && (
                <p className="mt-1 text-sm text-danger">{errors.companyId}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Value - Always editable */}
              <div>
                <label className="block text-sm font-semibold text-txt-secondary mb-2">
                  Opportunity Value ($) <span className="font-normal text-txt-tertiary">(Optional)</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.value}
                    onChange={(e) => handleInputChange('value', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all ${
                      errors.value ? 'border-danger bg-danger-soft' : 'border-strong'
                    }`}
                    placeholder={useProductValue && products.find(p => p.id === formData.productId) 
                      ? `Suggested: ${computeProductBasedValue(products.find(p => p.id === formData.productId), billingMonths)?.toLocaleString() || '0'}`
                      : "e.g., 50000 (optional)"}
                    disabled={isSubmitting} // Only disable during submission
                  />
                </div>
                {errors.value && (
                  <p className="mt-1 text-sm text-danger">{errors.value}</p>
                )}
                {useProductValue && products.find(p => p.id === formData.productId) && (
                  <p className="mt-1 text-xs text-accent">
                    💡 Product-based suggestion: {formatCurrency(computeProductBasedValue(products.find(p => p.id === formData.productId), billingMonths) || 0)}
                    {formData.value && Number(formData.value) !== computeProductBasedValue(products.find(p => p.id === formData.productId), billingMonths) && (
                      <span className="text-warning ml-2">⚠️ Custom value entered</span>
                    )}
                  </p>
                )}
              </div>

              {/* Probability */}
              <div>
                <label className="block text-sm font-semibold text-txt-secondary mb-2">
                  Close Probability (%)
                </label>
                <div className="relative">
                  <TrendingUp className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.probability}
                    onChange={(e) => handleInputChange('probability', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Expected Close Date */}
              <div>
                <label className="block text-sm font-semibold text-txt-secondary mb-2">
                  Expected Close Date *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                  <input
                    type="date"
                    value={formData.expectedCloseDate}
                    onChange={(e) => handleInputChange('expectedCloseDate', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all ${
                      errors.expectedCloseDate ? 'border-danger bg-danger-soft' : 'border-strong'
                    }`}
                    disabled={isSubmitting}
                  />
                </div>
                {errors.expectedCloseDate && (
                  <p className="mt-1 text-sm text-danger">{errors.expectedCloseDate}</p>
                )}
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-semibold text-txt-secondary mb-2">
                  Priority
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                  <select
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all appearance-none bg-bg-card"
                    disabled={isSubmitting}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Source */}
            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">
                Opportunity Source
              </label>
              <div className="grid grid-cols-3 gap-3">
                {['inbound', 'outbound', 'referral'].map((source) => (
                  <button
                    key={source}
                    type="button"
                    onClick={() => handleInputChange('source', source)}
                    className={`px-4 py-3 rounded-xl border transition-all ${
                      formData.source === source
                        ? 'border-accent bg-info-soft text-accent font-semibold'
                        : 'border-strong bg-bg-card text-txt-secondary hover:border-strong'
                    }`}
                    disabled={isSubmitting}
                  >
                    {source.charAt(0).toUpperCase() + source.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">
                Description (Optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all resize-none"
                rows={3}
                placeholder="Additional details about this opportunity..."
                disabled={isSubmitting}
              />
            </div>

            {/* Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-3 border border-strong text-txt-secondary rounded-xl hover:bg-bg-surface transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-3 bg-primary text-primary-contrast rounded-xl hover:bg-bg-card transition-all shadow-card disabled:opacity-50 flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create Opportunity
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      {isCompanyModalOpen && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsCompanyModalOpen(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-bg-card rounded-2xl shadow-heavy w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-default flex items-center justify-between">
                <h3 className="text-lg font-bold text-txt">Create Company</h3>
                <button className="p-2 hover:bg-bg-surface rounded-lg" onClick={() => setIsCompanyModalOpen(false)}>
                  <X className="w-5 h-5 text-txt-muted" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-txt-secondary mb-1">Name</label>
                  <input value={newCompany.name || ''} onChange={e => setNewCompany({...newCompany, name: e.target.value})} className="w-full border border-strong rounded-lg px-3 py-2 focus:ring-2 focus:ring-accent/30" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-txt-secondary mb-1">Industry</label>
                  <input value={newCompany.industry || ''} onChange={e => setNewCompany({...newCompany, industry: e.target.value})} className="w-full border border-strong rounded-lg px-3 py-2 focus:ring-2 focus:ring-accent/30" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-txt-secondary mb-1">Website</label>
                    <input value={newCompany.website || ''} onChange={e => setNewCompany({...newCompany, website: e.target.value})} className="w-full border border-strong rounded-lg px-3 py-2 focus:ring-2 focus:ring-accent/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-txt-secondary mb-1">Domain</label>
                    <input value={newCompany.domain || ''} onChange={e => setNewCompany({...newCompany, domain: e.target.value})} className="w-full border border-strong rounded-lg px-3 py-2 focus:ring-2 focus:ring-accent/30" />
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-default flex space-x-3 justify-end">
                <button onClick={() => setIsCompanyModalOpen(false)} className="px-4 py-2 border border-strong rounded-lg text-txt-secondary hover:bg-bg-surface">Cancel</button>
                <button onClick={async () => {
                  if (!newCompany.name) return;
                  const orgId = await usersService.getCurrentUserOrgId();
                  const id = await companiesService.createCompany({
                    id: '', // not used
                    name: newCompany.name!,
                    domain: newCompany.domain || '',
                    industry: newCompany.industry || '',
                    employeeCount: newCompany.employeeCount || '',
                    revenue: newCompany.revenue || '',
                    description: '',
                    website: newCompany.website || '',
                    socialProfiles: {},
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    orgId: orgId || undefined
                  } as any);
                  const updated = await companiesService.getCompanies(orgId || undefined);
                  setCompanies(updated);
                  setIsCompanyModalOpen(false);
                  setNewCompany({});
                  setFormData(prev => ({ ...prev, companyId: id }));
                }} className="px-4 py-2 bg-accent text-txt-inverted rounded-lg hover:bg-accent-bright">Create</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  , document.body);
};

export default CreateOpportunityModal;